import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { WifiOff, Wifi } from 'lucide-react';
import { API } from './utils';
import { useGuestPortal } from './GuestPortalContext';
import { syncFailedCheckinLogs } from './failedCheckinLog';

// Connection monitor for the kiosk tablet.
// Detects real internet loss (not just Wi-Fi interface state) by pinging the
// backend /health endpoint every 20s. Shows a prominent red banner when the
// tablet is offline so crews know it's a connection problem — not their fault —
// and a brief green "restored" banner when it comes back.
export function OfflineBanner() {
  const { language } = useGuestPortal();
  const [offline, setOffline] = useState(false);
  const [justRestored, setJustRestored] = useState(false);
  const failCount = useRef(0);
  const wasOffline = useRef(false);
  const restoredTimer = useRef(null);

  useEffect(() => {
    const markOnline = () => {
      failCount.current = 0;
      if (wasOffline.current) {
        wasOffline.current = false;
        setOffline(false);
        setJustRestored(true);
        syncFailedCheckinLogs();
        clearTimeout(restoredTimer.current);
        restoredTimer.current = setTimeout(() => setJustRestored(false), 6000);
      }
    };
    const markFailure = () => {
      failCount.current += 1;
      // Require 2 consecutive failures before alarming (avoids false alarms
      // from a single slow response), but go red instantly on the offline event.
      if (failCount.current >= 2) {
        wasOffline.current = true;
        setOffline(true);
      }
    };
    const heartbeat = async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        markFailure();
        return;
      }
      try {
        await axios.get(`${API}/health`, { timeout: 6000 });
        markOnline();
      } catch (e) {
        markFailure();
      }
    };
    const onOffline = () => {
      failCount.current = 2;
      wasOffline.current = true;
      setOffline(true);
    };
    const onOnline = () => heartbeat();

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    heartbeat();
    const interval = setInterval(heartbeat, 20000);
    // Flush any failed check-in logs queued during a previous outage
    syncFailedCheckinLogs();
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      clearInterval(interval);
      clearTimeout(restoredTimer.current);
    };
  }, []);

  if (offline) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white px-4 py-3 shadow-lg"
        data-testid="offline-banner"
      >
        <div className="flex items-center justify-center gap-3 animate-pulse">
          <WifiOff className="w-6 h-6 shrink-0" />
          <p className="font-bold text-sm sm:text-base text-center">
            {language === 'es'
              ? 'SIN CONEXIÓN A INTERNET — Por favor avise a recepción. El registro puede no funcionar.'
              : 'NO INTERNET CONNECTION — Please notify the front desk. Check-in may not work right now.'}
          </p>
        </div>
      </div>
    );
  }

  if (justRestored) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-[100] bg-green-600 text-white px-4 py-3 shadow-lg"
        data-testid="connection-restored-banner"
      >
        <div className="flex items-center justify-center gap-3">
          <Wifi className="w-6 h-6 shrink-0" />
          <p className="font-bold text-sm sm:text-base text-center">
            {language === 'es'
              ? 'Conexión restaurada — ya puede registrarse.'
              : 'Connection restored — you can check in now.'}
          </p>
        </div>
      </div>
    );
  }

  return null;
}
